"use client";

import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import hooks from "@/hooks";
import { Form, FormLabel } from "@/components/ui/form";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import { IVocation } from "@/actions/service/types";
import {
  UpdateServiceData,
  UpdateServiceFormSchema,
} from "@/components/shared/Forms/UpdateServiceForm/schema";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TimePicker } from "@/components/ui/datetime-picker";
import { format, parse } from "date-fns";

interface IUpdateServiceFormProps {
  service: IVocation;
  onSuccess?: () => void;
}

const UpdateServiceForm = ({ service, onSuccess }: IUpdateServiceFormProps) => {
  const { toast } = useToast();

  const { value, id, deleted_at, updated_at, observation, ...data } = service;

  const form = useForm<UpdateServiceData>({
    resolver: zodResolver(UpdateServiceFormSchema),
    defaultValues: {
      vocationId: id,
      value: Number(value),
      deleted_at: deleted_at ?? undefined,
      updated_at: updated_at ?? undefined,
      observation: observation ?? undefined,
      ...data,
    },
  });

  const {
    data: salonsData,
    isLoading: isLoadingSalons,
    isError: isErrorSalons,
  } = hooks.useGetSalons(1, 10);

  const salons = salonsData?.data ?? [];
  const duration = form.watch().duration;

  const updateService = hooks.useEditService({
    onSuccess: () => {
      toast({
        title: "Serviço atualizado com sucesso!",
        variant: "success",
      });
      if (onSuccess) onSuccess?.();
    },
    onError: (error: unknown) => {
      toast({
        title: "Não foi possível atualizar serviço",
        description: (error as Error).message,
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: UpdateServiceData) {
    await updateService.mutateAsync({
      vocationId: data.vocationId,
      name: data.name,
      value: data.value,
      duration: data.duration,
      observation: data.observation ?? "",
      salonId: data.salonId,
      deleted_at: data.deleted_at ?? undefined,
      updated_at: data.updated_at ?? undefined,
    });
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full flex flex-col items-center gap-y-6"
      >
        <div className={"w-full flex flex-col items-start"}>
          <FormLabel className={"font-semibold text-sm"}>Salão</FormLabel>
          <Controller
            control={form.control}
            name="salonId"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um salão" />
                </SelectTrigger>

                <SelectContent>
                  {isLoadingSalons && (
                    <SelectItem value={field.value} disabled>
                      Carregando salões...
                    </SelectItem>
                  )}

                  {!isLoadingSalons &&
                    !isErrorSalons &&
                    salons?.map((salon) => (
                      <SelectItem key={salon.id} value={salon.id}>
                        {salon.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <FormFields.InputFormField
          form={form}
          formName="name"
          placeholder="Insira o nome do serviço"
          label="Nome"
        />

        <FormFields.InputFormField
          form={form}
          formName="value"
          label="Insira o valor"
          placeholder="Valor"
          type="text"
          noClearButton
          mask="currency"
        />

        <div className={"w-full flex flex-col items-start"}>
          <FormLabel className={"font-semibold text-sm"}>Duração</FormLabel>
          <TimePicker
            key={`duration-${form.formState.submitCount}`}
            granularity="minute"
            date={parse(
              duration.replace(/^(\d{2})(\d{2})$/, "$1:$2"),
              "HH:mm",
              new Date(),
            )}
            onChange={(date) => {
              if (date) {
                const formattedTime = format(date, "HHmm");
                form.setValue("duration", formattedTime);
              }
            }}
          />
          {form.formState.errors.duration && (
            <p className="text-sm text-red-500">
              {form.formState.errors.duration.message}
            </p>
          )}
        </div>

        <FormFields.TextareaFormField
          form={form}
          formName="observation"
          label="Observação"
          rows={6}
        />

        <Buttons.DefaultButton
          className="py-6"
          type="submit"
          title="Salvar Alterações"
          isLoading={updateService.isPending}
        />
      </form>
    </Form>
  );
};

export default UpdateServiceForm;
